// OwnerToAgent Platform — Shared Type Definitions
// Model-driven: mirrors Prisma schema enums and entity shapes for frontend use.

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum Province {
  Gauteng = "gauteng",
  WesternCape = "western_cape",
  KwaZuluNatal = "kwazulu_natal",
  Limpopo = "limpopo",
  Mpumalanga = "mpumalanga",
  FreeState = "free_state",
  NorthWest = "north_west",
  EasternCape = "eastern_cape",
  NorthernCape = "northern_cape",
}

export enum PropertyType {
  Freehold = "freehold",
  Apartment = "apartment",
  Townhouse = "townhouse",
  Estate = "estate",
  Other = "other",
}

export enum Timeline {
  ASAP = "asap",
  ThisMonth = "this_month",
  ThreeToSix = "three_to_six",
  Exploring = "exploring",
}

export enum RequestType {
  Sale = "sale",
  Rental = "rental",
}

export enum ContactPreference {
  PlatformOnly = "platform_only",
  AfterApproval = "after_approval",
  NoDirectContact = "no_direct_contact",
}

export enum RequestStatus {
  Draft = "draft",
  Active = "active",
  ComparingAgents = "comparing_agents",
  ContactUnlocked = "contact_unlocked",
  Completed = "completed",
  Deactivated = "deactivated",
}

export enum PPRAStatus {
  Submitted = "submitted",
  Verified = "verified",
  Rejected = "rejected",
}

export enum DemandLevel {
  Low = "low",
  Medium = "medium",
  High = "high",
}

export enum SelectionStatus {
  Pending = "pending",
  Accepted = "accepted",
  Declined = "declined",
  Expired = "expired",
}

export enum SubscriptionTier {
  Free = "free",
  Starter = "starter",
  Professional = "professional",
  Growth = "growth",
  AgencyNetwork = "agency_network",
}

export enum SubscriptionStatus {
  Pending = "pending",
  Active = "active",
  Cancelled = "cancelled",
  PastDue = "past_due",
  Trialing = "trialing",
}

export enum TrustScoreEventType {
  ResponseSubmitted = "response_submitted",
  FeedbackReceived = "feedback_received",
  UnlockCompleted = "unlock_completed",
  SpamReported = "spam_reported",
  ProfileUpdated = "profile_updated",
  VerificationChanged = "verification_changed",
}

export enum VerificationAction {
  Approve = "approve",
  Reject = "reject",
  Revoke = "revoke",
  Expire = "expire",
}

export type VerificationLevel = 1 | 2 | 3;

export type AgentBadge =
  | "ppra_verified"
  | "trusted_pro"
  | "local_specialist"
  | "fast_responder"
  | "top_rated";

export interface TrustScoreBreakdown {
  ffc_verification: number;
  seller_ratings: number;
  response_speed: number;
  conversion_rate: number;
  activity: number;
}

export interface SellerFeedback {
  id: string;
  agent_id: string;
  rating: number;
  review?: string | null;
  reviewer: string; // always "Verified Seller"
  created_at: string;
}

export interface FeedbackSummary {
  average_rating: number;
  total_reviews: number;
}

export interface VerificationAuditLog {
  id: string;
  agent_id: string;
  admin_id: string;
  action: VerificationAction;
  previous_status: PPRAStatus;
  new_status: PPRAStatus;
  reason?: string | null;
  created_at: string;
}

// ─── Display Helpers ──────────────────────────────────────────────────────────

export const ProvinceLabels: Record<Province, string> = {
  [Province.Gauteng]: "Gauteng",
  [Province.WesternCape]: "Western Cape",
  [Province.KwaZuluNatal]: "KwaZulu-Natal",
  [Province.Limpopo]: "Limpopo",
  [Province.Mpumalanga]: "Mpumalanga",
  [Province.FreeState]: "Free State",
  [Province.NorthWest]: "North West",
  [Province.EasternCape]: "Eastern Cape",
  [Province.NorthernCape]: "Northern Cape",
};

export const PropertyTypeLabels: Record<PropertyType, string> = {
  [PropertyType.Freehold]: "Freehold",
  [PropertyType.Apartment]: "Apartment",
  [PropertyType.Townhouse]: "Townhouse",
  [PropertyType.Estate]: "Estate",
  [PropertyType.Other]: "Other",
};

export const TimelineLabels: Record<Timeline, string> = {
  [Timeline.ASAP]: "ASAP",
  [Timeline.ThisMonth]: "This month",
  [Timeline.ThreeToSix]: "3–6 months",
  [Timeline.Exploring]: "Just exploring",
};

export const RequestStatusLabels: Record<RequestStatus, string> = {
  [RequestStatus.Draft]: "Draft",
  [RequestStatus.Active]: "Active",
  [RequestStatus.ComparingAgents]: "Comparing Agents",
  [RequestStatus.ContactUnlocked]: "Contact Unlocked",
  [RequestStatus.Completed]: "Completed",
  [RequestStatus.Deactivated]: "Deactivated",
};

export const PPRAStatusLabels: Record<PPRAStatus, string> = {
  [PPRAStatus.Submitted]: "Awaiting Review",
  [PPRAStatus.Verified]: "PPRA Verified",
  [PPRAStatus.Rejected]: "Rejected",
};

// ─── Entity Interfaces ────────────────────────────────────────────────────────

export interface OwnerRequest {
  id: string;
  suburb: string;
  province: Province;
  property_type: PropertyType;
  price_band_min: number;
  price_band_max: number;
  timeline: Timeline;
  request_type: RequestType;
  contact_preference: ContactPreference;
  seller_notes?: string | null;
  status: RequestStatus;
  created_at: string;
  updated_at: string;
  response_count?: number;
  engaging_count?: number;
  contact_shared_count?: number;
  completed_count?: number;
  invited_count?: number;
  my_response_status?: string | null;
}

export interface AgentProfile {
  id: string;
  display_name: string;
  agency_name: string;
  ppra_ffc_number?: string | null;
  ppra_status: PPRAStatus;
  years_experience?: number | null;
  bio?: string | null;
  profile_photo_url?: string | null;
  agency_logo_url?: string | null;
  primary_region: Province;
  subscribed_suburbs: string[];
  trust_score: number;
  avg_response_time_mins?: number | null;
  total_responses: number;
  total_unlocks: number;
  verification_level?: VerificationLevel;
  badges?: AgentBadge[];
  ffc_expiry_date?: string | null;
  phone_number?: string | null;
  created_at: string;
}

export interface AgentResponse {
  id: string;
  request_id: string;
  agent_id: string;
  valuation_min: number;
  valuation_max: number;
  demand_level: DemandLevel;
  comparable_sales: ComparableSale[];
  strategy_summary: string;
  commission_rate?: number | null;
  created_at: string;
  agent?: AgentProfile;
}

export interface ComparableSale {
  address: string;
  price: number;
  date: string;
}

export interface Selection {
  id: string;
  request_id: string;
  response_id: string;
  agent_id: string;
  status: SelectionStatus;
  unlock_fee_cents: number;
  invited_at: string;
  responded_at?: string | null;
  expires_at: string;
  agent?: AgentProfile;
  response?: AgentResponse;
}

export interface ContactUnlock {
  id: string;
  selection_id: string;
  request_id: string;
  agent_id: string;
  qr_token: string;
  contact_released: ContactInfo;
  consent_reference: string;
  consented_at: string;
  revoked_at?: string | null;
  agent?: AgentProfile;
}

export interface ContactInfo {
  phone?: string;
  email?: string;
  whatsapp?: string;
}

export interface Subscription {
  id: string;
  agent_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  regions: Province[];
  lead_limit: number;
  leads_used: number;
  leads_remaining: number;
  trial_ends_at?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  scheduled_tier?: SubscriptionTier | null;
  start_date: string;
  end_date: string;
}

// ─── API Request/Response Types ───────────────────────────────────────────────

export interface CreateOwnerRequestPayload {
  suburb: string;
  province: Province;
  property_type: PropertyType;
  price_band_min: number;
  price_band_max: number;
  timeline: Timeline;
  request_type: RequestType;
  contact_preference: ContactPreference;
  seller_notes?: string;
}

export interface UpdateOwnerRequestPayload {
  suburb?: string;
  province?: Province;
  property_type?: PropertyType;
  price_band_min?: number;
  price_band_max?: number;
  timeline?: Timeline;
  request_type?: RequestType;
  contact_preference?: ContactPreference;
  seller_notes?: string;
  status?: RequestStatus;
}

export interface CreateAgentProfilePayload {
  display_name: string;
  agency_name: string;
  ppra_ffc_number: string;
  years_experience?: number;
  bio?: string;
  phone_number?: string;
  primary_region: Province;
  subscribed_suburbs?: string[];
}

export interface UpdateAgentProfilePayload {
  display_name?: string;
  agency_name?: string;
  years_experience?: number;
  bio?: string;
  primary_region?: Province;
  subscribed_suburbs?: string[];
  profile_photo_url?: string;
  agency_logo_url?: string;
}

export interface CreateResponsePayload {
  request_id: string;
  valuation_min?: number;
  valuation_max?: number;
  demand_level: DemandLevel;
  comparable_sales: ComparableSale[];
  strategy_summary: string;
  commission_rate?: number;
}

export interface CreateSelectionsPayload {
  request_id: string;
  response_ids: string[];
}

export interface BrowseRequestsQuery {
  province?: Province;
  suburb?: string;
  property_type?: PropertyType;
  timeline?: Timeline;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Array<{ field: string; issue: string }>;
  };
}

// ─── Auth Types ───────────────────────────────────────────────────────────────

export type UserRole = "owner" | "agent" | "admin";

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  name?: string;
}

// ─── Inventory Types ──────────────────────────────────────────────────────────

export interface InventorySummary {
  province: Province;
  suburbs: SuburbDemand[];
  total_active: number;
}

export interface SuburbDemand {
  suburb: string;
  active_requests: number;
  avg_price_band: number;
  dominant_property_type: PropertyType;
  dominant_timeline: Timeline;
}

// ─── Payment & Billing Types ──────────────────────────────────────────────────

export type IntentLevel = "low" | "medium" | "high" | "hot";

export type TransactionType = "subscription" | "contact_unlock" | "boost" | "upgrade_proration" | "refund";

export type TransactionStatus = "pending" | "completed" | "failed" | "refunded";

export type BoostType = "daily" | "weekly" | "monthly";

export type BoostStatus = "active" | "expired" | "cancelled";

export interface Transaction {
  id: string;
  agent_id: string;
  type: TransactionType;
  amount_cents: number;
  amount_display: string;
  currency: string;
  status: TransactionStatus;
  payment_ref?: string | null;
  description: string;
  created_at: string;
}

export interface Boost {
  id: string;
  agent_id: string;
  suburb: string;
  boost_type: BoostType;
  status: BoostStatus;
  start_date: string;
  end_date: string;
  ranking_multiplier: number;
  created_at: string;
}

export interface UnlockPrice {
  intent_level: IntentLevel;
  price_cents: number;
  price_display: string;
  leads_remaining: number;
  can_unlock: boolean;
  block_reason?: string | null;
}

export interface PlanChangeResponse {
  change_type: "upgrade" | "downgrade";
  from_tier: string;
  to_tier: string;
  prorated_amount_cents?: number;
  effective_date?: string;
  authorization_url?: string;
}
